import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSetExerciseGoal } from '../../../queries/exercise-goal-queries'
import { showAlert } from '../../../utils/alert'
import { useExerciseGoal } from './useStatisticsViewModel'

const EXERCISE_GOAL_FALLBACK_VALUE = 4
const SELECTION_FEEDBACK_DELAY_MS = 220
const PLACEHOLDER_VALUE = '--'

export function useStatisticsScreenController() {
  const { t } = useTranslation(['statistics', 'common'])
  const { exerciseGoal } = useExerciseGoal()
  const setExerciseGoalMutation = useSetExerciseGoal()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isSelectionTransitioning, setIsSelectionTransitioning] =
    useState(false)
  const [optimisticGoalValue, setOptimisticGoalValue] = useState<number | null>(
    null,
  )
  const [selectedGoalValue, setSelectedGoalValue] = useState(
    exerciseGoal.currentValue ?? EXERCISE_GOAL_FALLBACK_VALUE,
  )

  const formatExerciseGoalValue = useCallback(
    (goalValue: number | null): string => {
      if (goalValue == null) {
        return PLACEHOLDER_VALUE
      }

      return t('statistics:exercise_goal_value_per_week', {
        count: goalValue,
      })
    },
    [t],
  )

  const displayedGoalValue = useMemo(() => {
    return optimisticGoalValue ?? exerciseGoal.currentValue
  }, [exerciseGoal.currentValue, optimisticGoalValue])

  const exerciseGoalView = useMemo(() => {
    return {
      ...exerciseGoal,
      currentValue: displayedGoalValue,
      value: formatExerciseGoalValue(displayedGoalValue),
    }
  }, [displayedGoalValue, exerciseGoal, formatExerciseGoalValue])

  const effectiveGoalValue = useMemo(() => {
    return displayedGoalValue ?? EXERCISE_GOAL_FALLBACK_VALUE
  }, [displayedGoalValue])

  const openExerciseGoalModal = useCallback(() => {
    setSelectedGoalValue(effectiveGoalValue)
    setIsModalVisible(true)
  }, [effectiveGoalValue])

  const closeExerciseGoalModal = useCallback(() => {
    if (setExerciseGoalMutation.isPending || isSelectionTransitioning) {
      return
    }
    setIsModalVisible(false)
  }, [isSelectionTransitioning, setExerciseGoalMutation.isPending])

  const handleSelectExerciseGoal = useCallback(
    async (value: number) => {
      if (setExerciseGoalMutation.isPending || isSelectionTransitioning) {
        return
      }

      setIsSelectionTransitioning(true)
      setSelectedGoalValue(value)
      setOptimisticGoalValue(value)

      try {
        const mutationResultPromise = setExerciseGoalMutation
          .mutateAsync(value)
          .then(() => ({ success: true as const }))
          .catch((error: unknown) => ({ success: false as const, error }))

        await new Promise((resolve) =>
          setTimeout(resolve, SELECTION_FEEDBACK_DELAY_MS),
        )
        setIsModalVisible(false)

        const mutationResult = await mutationResultPromise

        if (!mutationResult.success) {
          console.error('Failed to update exercise goal', mutationResult.error)
          setOptimisticGoalValue(null)
          showAlert(
            t('common:error'),
            t('statistics:exercise_goal_update_failed'),
          )
        }
      } finally {
        setIsSelectionTransitioning(false)
      }
    },
    [isSelectionTransitioning, setExerciseGoalMutation, t],
  )

  return {
    exerciseGoal: exerciseGoalView,
    isModalVisible,
    modalTitle: t('statistics:exercise_goal_modal_title'),
    selectedGoalValue,
    isSavingGoal: setExerciseGoalMutation.isPending || isSelectionTransitioning,
    openExerciseGoalModal,
    closeExerciseGoalModal,
    handleSelectExerciseGoal,
  }
}
